import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Gift,
  Package,
  Truck,
  MapPin,
  CheckCircle2,
  Star,
  Sparkles,
  Crown,
  Gem,
  Target,
  Trophy,
  Zap,
  Eye,
  Calendar,
  AlertCircle
} from 'lucide-react'
import { Button, Input } from '../ui'
import type { Achievement } from '../../types/loyalty'
import type { User } from '../../types/user'

interface PremiumClaimModalProps {
  isOpen: boolean
  onClose: () => void
  achievement: Achievement | null
  user?: User | null
  onConfirmClaim: (achievement: Achievement, shippingData: ShippingData) => Promise<void>
}

interface ShippingData {
  firstName: string
  lastName: string
  email: string
  phone: string
  address: string
  city: string
  postalCode: string
  country: string
}

const PremiumClaimModal = ({
  isOpen,
  onClose,
  achievement,
  user,
  onConfirmClaim
}: PremiumClaimModalProps) => {
  const [step, setStep] = useState<'overview' | 'shipping' | 'confirmation'>('overview')
  const [isLoading, setIsLoading] = useState(false)

  // Initialize shipping data with user profile data
  const getInitialShippingData = (): ShippingData => {
    const defaultAddress = user?.defaultShippingAddress
    return {
      firstName: user?.firstName || defaultAddress?.firstName || '',
      lastName: user?.lastName || defaultAddress?.lastName || '',
      email: user?.email || defaultAddress?.email || '',
      phone: user?.phoneNumber || defaultAddress?.phone || '',
      address: defaultAddress ? `${defaultAddress.addressLine1} ${defaultAddress.number}` : '',
      city: defaultAddress?.city || '',
      postalCode: defaultAddress?.postalCode || '',
      country: defaultAddress?.country || 'España'
    }
  }

  const [shippingData, setShippingData] = useState<ShippingData>(getInitialShippingData())

  // Update shipping data when user changes
  useEffect(() => {
    if (user) {
      setShippingData(getInitialShippingData())
    }
  }, [user])

  if (!achievement) return null

  const getAchievementIcon = () => {
    switch (achievement.level) {
      case 'explorer': return Star
      case 'hunter': return Target
      case 'champion': return Crown
      case 'diamond': return Gem
      default: return Trophy
    }
  }

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'product': return Package
      case 'discount': return Zap
      case 'access': return Eye
      default: return Gift
    }
  }

  const getFrameGradient = () => {
    switch (achievement.level) {
      case 'explorer': return 'from-amber-400 to-amber-600'
      case 'hunter': return 'from-gray-400 to-gray-600'
      case 'champion': return 'from-yellow-400 to-yellow-600'
      case 'diamond': return 'from-cyan-400 to-cyan-600'
      default: return 'from-gray-400 to-gray-600'
    }
  }

  const handleInputChange = (field: keyof ShippingData, value: string) => {
    setShippingData(prev => ({ ...prev, [field]: value }))
  }

  const isShippingFormValid = () => {
    const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'postalCode']
    return required.every(field => shippingData[field as keyof ShippingData].trim() !== '')
  }

  const handleConfirmClaim = async () => {
    if (!isShippingFormValid()) return

    setIsLoading(true)
    try {
      await onConfirmClaim(achievement, shippingData)
      setStep('confirmation')
    } catch (error) {
      console.error('Error claiming reward:', error)
      // Handle error - show toast or error message
    } finally {
      setIsLoading(false)
    }
  }

  const resetModal = () => {
    setStep('overview')
    if (user) {
      setShippingData(getInitialShippingData())
    }
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  const AchievementIcon = getAchievementIcon()

  if (!isOpen) return null

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="claim-modal-title"
          aria-describedby="claim-modal-description"
          style={{ margin: 0, padding: 0 }}
        >
          <div className="h-screen w-full flex items-stretch justify-center p-0 sm:p-4 lg:p-8">
            <motion.div
              className="relative w-full sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl bg-white shadow-2xl sm:rounded-2xl h-full sm:h-auto sm:max-h-[95vh]"
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col h-full max-h-full">
                {/* Header fijo */}
                <div className="flex-shrink-0 p-4 sm:p-6 lg:p-8 pb-0">
                  {/* Botón cerrar */}
                  <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 w-10 h-10 bg-white hover:bg-gray-50 rounded-full shadow-md border border-gray-200 flex items-center justify-center transition-all duration-200 hover:scale-105 z-10"
                    aria-label="Cerrar modal de reclamación de recompensas"
                    type="button"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Contenido con scroll */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8">
                  <div className="space-y-6">
        {/* Header */}
        <div className="text-center relative">

          {/* Premium Badge */}
          <motion.div
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-4 py-2 rounded-full font-bold text-sm mb-4"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
          >
            <Sparkles className="w-4 h-4" />
            <span>RECOMPENSA PREMIUM</span>
            <Sparkles className="w-4 h-4" />
          </motion.div>

          <motion.div
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br ${getFrameGradient()} text-white mb-4 ring-4 ring-white/20`}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", duration: 0.6, delay: 0.2 }}
          >
            <AchievementIcon className="w-10 h-10" />
          </motion.div>

          <h2
            id="claim-modal-title"
            className="text-xl sm:text-2xl font-bold text-gray-900 mb-2"
          >
            {step === 'overview' && 'Reclamar Recompensas'}
            {step === 'shipping' && 'Información de Envío'}
            {step === 'confirmation' && '¡Recompensas Reclamadas!'}
          </h2>

          <p
            id="claim-modal-description"
            className="text-sm sm:text-base text-gray-600"
          >
            {step === 'overview' && `Logro ${achievement.name} - Nivel ${achievement.level}`}
            {step === 'shipping' && 'Completa tus datos para recibir las recompensas'}
            {step === 'confirmation' && 'Tu solicitud ha sido procesada correctamente'}
          </p>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          {/* Overview Step */}
          {step === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              {/* Achievement Info */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-yellow-600" />
                  Resumen del Logro
                </h3>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nivel:</span>
                    <span className="font-semibold capitalize">{achievement.level}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Puntos requeridos:</span>
                    <span className="font-semibold">{achievement.pointsRequired}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha de desbloqueo:</span>
                    <span className="font-semibold">
                      {achievement.unlockedAt?.toLocaleDateString('es-ES')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tipo de recompensa:</span>
                    <span className="font-semibold text-green-600">Marco + Recompensas Físicas</span>
                  </div>
                </div>
              </div>

              {/* Rewards List */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Gift className="w-5 h-5 mr-2 text-green-600" />
                  Recompensas Incluidas ({achievement.physicalRewards.length})
                </h3>

                <div className="grid grid-cols-1 gap-4">
                  {achievement.physicalRewards.map((reward, index) => {
                    const IconComponent = getRewardIcon(reward.type)
                    return (
                      <motion.div
                        key={reward.id}
                        className="flex items-center space-x-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center">
                          <IconComponent className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{reward.name}</h4>
                          <p className="text-sm text-gray-600">{reward.description}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                              {reward.type === 'product' ? 'Producto' :
                               reward.type === 'discount' ? 'Descuento' : 'Acceso'}
                            </span>
                            <span className="text-xs text-gray-500">Valor: {reward.value}</span>
                          </div>
                        </div>
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                      </motion.div>
                    )
                  })}
                </div>
              </div>

              {/* Important Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900">Información Importante</h4>
                    <ul className="text-sm text-blue-800 mt-2 space-y-1">
                      <li>• Las recompensas se enviarán en un plazo de 5-7 días laborables</li>
                      <li>• Los productos pueden variar según la disponibilidad</li>
                      <li>• Solo puedes reclamar cada logro una vez</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Continue Button */}
              <Button
                onClick={() => setStep('shipping')}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold text-lg py-3"
                size="lg"
              >
                <Truck className="w-5 h-5 mr-2" />
                Continuar con Envío
              </Button>
            </motion.div>
          )}

          {/* Shipping Step */}
          {step === 'shipping' && (
            <motion.div
              key="shipping"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">
                    Completa tu información de envío
                  </span>
                </div>
              </div>

              <form className="space-y-4 sm:space-y-6" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Nombre *"
                    placeholder="Tu nombre"
                    value={shippingData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                  <Input
                    label="Apellidos *"
                    placeholder="Tus apellidos"
                    value={shippingData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Email *"
                    type="email"
                    placeholder="tu@email.com"
                    value={shippingData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    aria-describedby="email-help"
                  />
                  <Input
                    label="Teléfono *"
                    placeholder="+34 123 456 789"
                    value={shippingData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    aria-describedby="phone-help"
                  />
                </div>

                <Input
                  label="Dirección completa *"
                  placeholder="Calle, número, piso, puerta..."
                  value={shippingData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  required
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input
                    label="Ciudad *"
                    placeholder="Tu ciudad"
                    value={shippingData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    required
                  />
                  <Input
                    label="Código Postal *"
                    placeholder="12345"
                    value={shippingData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    required
                  />
                  <Input
                    label="País *"
                    value={shippingData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    required
                  />
                </div>

              </form>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  onClick={() => setStep('overview')}
                  variant="outline"
                  className="flex-1 order-2 sm:order-1"
                  type="button"
                >
                  Volver
                </Button>
                <Button
                  onClick={handleConfirmClaim}
                  disabled={!isShippingFormValid()}
                  isLoading={isLoading}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold order-1 sm:order-2"
                  type="submit"
                  aria-describedby="form-submit-help"
                >
                  <Gift className="w-5 h-5 mr-2" />
                  Confirmar Reclamación
                </Button>
              </div>
            </motion.div>
          )}

          {/* Confirmation Step */}
          {step === 'confirmation' && (
            <motion.div
              key="confirmation"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-6"
            >
              <motion.div
                className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.6, delay: 0.2 }}
              >
                <CheckCircle2 className="w-12 h-12 text-white" />
              </motion.div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  ¡Recompensas Reclamadas con Éxito!
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Hemos procesado tu solicitud de recompensas para el logro <strong>{achievement.name}</strong>.
                  Nuestro equipo procesará tu pedido y te mantendremos informado del estado del envío.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h4 className="font-semibold text-green-900 mb-3">Próximos pasos:</h4>
                <div className="space-y-2 text-sm text-green-800">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Solicitud recibida y confirmada</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Procesamiento: 1-2 días laborables</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Truck className="w-4 h-4" />
                    <span>Envío: 5-7 días laborables</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold"
                size="lg"
              >
                ¡Genial! Cerrar
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return createPortal(modalContent, document.body)
}

export default PremiumClaimModal